import { Plus, Pencil, Trash2 } from 'lucide-react'
import { DataTable, DataTableColumn } from '@/components/common/DataTable'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { DriverAdjustmentSummary } from '@/hooks/useWizardAdjustmentsController'
import {
  Compensation,
  getCompensationCategoryInfo,
  COMPENSATION_APPLICATION_TARGETS
} from '@/types/compensation'
import { Earning } from '@/types/earnings'
import { cn } from '@/lib/utils'

interface DriverAdjustmentsTableProps {
  summaries: DriverAdjustmentSummary[]
  expandedDriverIds: Set<string>
  excludedDriverIds: Set<string>
  onToggleExpand: (driverId: string) => void
  onToggleExclusion: (driverId: string) => void
  onAddCompensation: (driverId: string) => void
  onEditCompensation: (compensation: Compensation) => void
  onDeleteCompensation: (compensationId: string) => Promise<void>
  onDeleteEarning: (earningId: string) => Promise<void>
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
}

function formatPlatformName(platform: string): string {
  const names: Record<string, string> = {
    uber: 'Uber',
    bolt: 'Bolt',
    fair: 'Fair',
    hack_rides: 'Hack Rides',
    'friåkning': 'Friåkning',
    other: 'Annat'
  }
  return names[platform.toLowerCase()] || platform
}

function getApplyToLabel(applyTo: string): string {
  const target = COMPENSATION_APPLICATION_TARGETS.find(t => t.value === applyTo)
  return target?.label || applyTo
}

export function DriverAdjustmentsTable({
  summaries,
  expandedDriverIds,
  excludedDriverIds,
  onToggleExpand,
  onToggleExclusion,
  onAddCompensation,
  onEditCompensation,
  onDeleteCompensation,
  onDeleteEarning
}: DriverAdjustmentsTableProps) {
  const columns: DataTableColumn<DriverAdjustmentSummary>[] = [
    {
      key: 'include',
      header: 'Inkludera i period',
      width: '140px',
      cell: (_value, row) => {
        const isExcluded = excludedDriverIds.has(row.driverId)
        return (
          <Checkbox
            checked={!isExcluded}
            onCheckedChange={() => onToggleExclusion(row.driverId)}
            onClick={(e) => e.stopPropagation()}
            aria-label={isExcluded ? 'Inkludera förare' : 'Exkludera förare'}
          />
        )
      }
    },
    {
      key: 'driverName',
      header: 'Förare',
      cell: (value, row) => {
        const isExcluded = excludedDriverIds.has(row.driverId)
        return (
          <span className={cn('font-medium', isExcluded && 'line-through text-muted-foreground')}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'totalNetEarnings',
      header: 'Nettointäkter',
      align: 'right',
      cell: (value) => `${formatCurrency(value)} kr`
    },
    {
      key: 'totalCompensations',
      header: 'Ersättningar',
      align: 'right',
      cell: (value: number) => value > 0 ? (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          +{formatCurrency(value)}
        </Badge>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'totalDeductions',
      header: 'Avdrag',
      align: 'right',
      cell: (value: number) => value > 0 ? (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          -{formatCurrency(value)}
        </Badge>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '100px',
      cell: (_value, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onAddCompensation(row.driverId)
          }}
          className="gap-1 h-8"
        >
          <Plus className="h-4 w-4" />
          Lägg till
        </Button>
      )
    }
  ]

  const createExpandableContent = (summary: DriverAdjustmentSummary) => {
    const compensations = summary.compensations.filter(c => c.type === 'compensation')
    const deductions = summary.compensations.filter(c => c.type === 'deduction')
    const totalEarnings = summary.earningsByPlatform.reduce((sum, p) => sum + p.earnings.length, 0)

    return (
      <div className="space-y-4">
        {/* Earnings breakdown by platform */}
        {summary.earningsByPlatform.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-2">
              Intäkter per plattform ({totalEarnings} {totalEarnings === 1 ? 'post' : 'poster'})
            </div>
            <div className="space-y-2">
              {summary.earningsByPlatform.map(({ platform, netEarnings, earnings }) => (
                <div key={platform} className="border rounded p-2 bg-background">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{formatPlatformName(platform)}</span>
                    <span className="text-sm font-medium">{formatCurrency(netEarnings)} kr</span>
                  </div>
                  {earnings.length === 1 ? (
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        Brutto: {formatCurrency(earnings[0].grossEarnings)} kr
                        {earnings[0].tips > 0 && ` · Dricks: ${formatCurrency(earnings[0].tips)} kr`}
                      </span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Ta bort intäkt?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Detta tar bort {formatPlatformName(platform)}-intäkten på {formatCurrency(earnings[0].netEarnings)} kr netto.
                              Åtgärden kan inte ångras.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Avbryt</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDeleteEarning(earnings[0].id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Ta bort
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ) : (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Visa {earnings.length} poster
                      </summary>
                      <div className="mt-1 space-y-1">
                        {earnings.map(earning => (
                          <div key={earning.id} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50">
                            <span>
                              Netto: {formatCurrency(earning.netEarnings)} kr
                              {earning.tips > 0 && ` · Dricks: ${formatCurrency(earning.tips)} kr`}
                            </span>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Ta bort intäkt?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Detta tar bort {formatPlatformName(platform)}-intäkten på {formatCurrency(earning.netEarnings)} kr netto.
                                    Åtgärden kan inte ångras.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => onDeleteEarning(earning.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Ta bort
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compensations list */}
        {compensations.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-2">
              Ersättningar ({compensations.length})
            </div>
            <div className="space-y-1">
              {compensations.map(comp => (
                <CompensationRow
                  key={comp.id}
                  compensation={comp}
                  onEdit={() => onEditCompensation(comp)}
                  onDelete={() => onDeleteCompensation(comp.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Deductions list */}
        {deductions.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-2">
              Avdrag ({deductions.length})
            </div>
            <div className="space-y-1">
              {deductions.map(comp => (
                <CompensationRow
                  key={comp.id}
                  compensation={comp}
                  onEdit={() => onEditCompensation(comp)}
                  onDelete={() => onDeleteCompensation(comp.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <DataTable
      data={summaries}
      columns={columns}
      expandableContent={createExpandableContent}
      expandedRows={expandedDriverIds}
      onRowExpand={(id) => onToggleExpand(String(id))}
      getRowId={(row) => row.driverId}
      emptyMessage="Inga förare med intäkter eller justeringar för denna period."
    />
  )
}

interface CompensationRowProps {
  compensation: Compensation
  onEdit: () => void
  onDelete: () => void
}

function CompensationRow({ compensation, onEdit, onDelete }: CompensationRowProps) {
  const categoryInfo = getCompensationCategoryInfo(compensation.category)
  const isDeduction = compensation.type === 'deduction'

  return (
    <div className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/50 group text-sm">
      <div className="flex items-center gap-2">
        <span className={cn(
          'font-medium',
          isDeduction ? 'text-red-700' : 'text-green-700'
        )}>
          {isDeduction ? '-' : '+'}{formatCurrency(compensation.amount)} kr
        </span>
        <span className="text-muted-foreground">
          ({categoryInfo.label})
        </span>
        <span className="text-muted-foreground">
          &rarr; {getApplyToLabel(compensation.applyTo)}
        </span>
        {compensation.comment && (
          <span className="text-xs text-muted-foreground italic truncate max-w-48">
            {compensation.comment}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive hover:text-destructive"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Ta bort {isDeduction ? 'avdrag' : 'ersättning'}?</AlertDialogTitle>
              <AlertDialogDescription>
                Detta tar bort {formatCurrency(compensation.amount)} kr ({categoryInfo.label}).
                Åtgärden kan inte ångras.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Ta bort
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
